import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import {
  GlobalBlock,
  GlobalBlocksV,
  GlobalBlockV
} from "@/types/global-blocks";
import { _Error } from "@/lib/wordpress/data";
import { Either, fold } from "fp-ts/lib/Either";
import { ExportedTree, TreeNode } from "@/types/tree";
import {
  copyNode,
  createNode,
  exportTree,
  findNode,
  getBlankTree,
  getNodeIndex,
  getNodeParent,
  moveNode
} from "@/lib/tree/tree";
import { configStore, documentStore, uiStore } from "@/store/store";
import { pipe } from "fp-ts/lib/function";
import { DIV_SLUG, GLOBAL_BLOCK_SLUG } from "@/lib/elements/constants";
import defensiveCopy from "@/util/defensive-copy";
import {
  getNestingRuleFromElementSlug,
  isNestingRuleAllowedInside,
  isThisNodeAllowedInsideThatNode
} from "@/lib/tree/nesting";
import NotificationLogger from "@/lib/notifications/notifications";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { breakdanceStoreActions } from "@/store/actions/store-actions";

export async function getBlocksFromWordPress(): Promise<
  Either<_Error[], GlobalBlock[]>
> {
  return await makeWordPressAjaxRequest({
    action: "breakdance_load_blocks",
    resultKey: "blocks",
    decoder: GlobalBlocksV
  });
}

// TODO this creates multiple snapshots, should be wrapped with transaction
export async function convertToGlobalBlock(
  nodeIdOfElementToConvert: number,
  blockName: string
) {
  const nodeToConvert = findNode(
    documentStore.document.tree,
    nodeIdOfElementToConvert
  );
  if (!nodeToConvert) return;

  const globalBlockTree = createGlobalBlockTreeFromNode(nodeToConvert);

  uiStore.setElementUpdatingState({
    elementId: nodeIdOfElementToConvert,
    isUpdating: true
  });

  pipe(
    await saveGlobalBlockToWordPress({
      tree: globalBlockTree,
      label: blockName,
      id: -1,
      editInBreakdanceLink: ""
    }),
    fold(
      errors => {
        errors.forEach(e => NotificationLogger.log.message(e.msg));

        uiStore.setElementUpdatingState({
          elementId: nodeIdOfElementToConvert,
          isUpdating: false
        });
      },
      savedBlockInfo => {
        const index = getNodeIndex(
          documentStore.document.tree,
          nodeToConvert.id
        );
        const parent = getNodeParent(
          documentStore.document.tree,
          nodeToConvert.id
        );

        uiStore.setElementUpdatingState({
          elementId: nodeIdOfElementToConvert,
          isUpdating: false
        });

        if (!parent) return;

        if (
          !isNestingRuleAllowedInside(
            getNestingRuleFromElementSlug(GLOBAL_BLOCK_SLUG),
            getNestingRuleFromElementSlug(parent.data.type),
            parent.data.type,
            "breakdance_block"
          )
        ) {
          return;
        }

        const pointerForCreatedNodeId: { nodeId?: number } = {
          nodeId: undefined
        };

        // TODO these create 3 snapshots. Would be great if they were a single one to easily undo/redo
        documentStore.addElementMutation({
          elementSlug: GLOBAL_BLOCK_SLUG,
          parentNodeId: parent.id,
          index: index,
          pointer: pointerForCreatedNodeId
        });

        if (!pointerForCreatedNodeId.nodeId) {
          return;
        }

        breakdanceStoreActions.run.onCreatedElement({
          elementId: pointerForCreatedNodeId.nodeId,
          transactionKey: undefined
        });

        documentStore.deleteElement({ nodeId: nodeIdOfElementToConvert });

        documentStore.throttledPropertyChanged({
          elementId: pointerForCreatedNodeId.nodeId,
          path: "content.content.block",
          value: savedBlockInfo.id,
          meta: {
            // TODO write something meaningful
            snapshotLabel: "Replace element with a global block"
          }
        });

        uiStore.activateElement(pointerForCreatedNodeId.nodeId);
      }
    )
  );
}

function createGlobalBlockTreeFromNode(node: TreeNode): ExportedTree {
  const tree = getBlankTree();
  const copiedNode = copyNode(tree, node);

  const isAllowedInsideRoot = isThisNodeAllowedInsideThatNode(
    tree,
    copiedNode,
    tree.root.id,
    "breakdance_block"
  );

  if (isAllowedInsideRoot) {
    moveNode(
      tree,
      copiedNode.id,
      tree.root.id,
      tree.root.children.length,
      "breakdance_block"
    );
  } else {
    const divWrapperNode = createNode(tree, {
      type: DIV_SLUG,
      properties: configStore.element(DIV_SLUG).defaultProperties
        ? defensiveCopy(configStore.element(DIV_SLUG).defaultProperties)
        : null
    });
    moveNode(
      tree,
      divWrapperNode.id,
      tree.root.id,
      tree.root.children.length,
      "breakdance_block"
    );
    moveNode(
      tree,
      copiedNode.id,
      divWrapperNode.id,
      divWrapperNode.children.length,
      "breakdance_block"
    );
  }

  return exportTree(tree);
}

export async function saveGlobalBlockToWordPress(globalBlock: GlobalBlock) {
  return await makeWordPressAjaxRequest({
    friendlyName: "Saving Global Block to WordPress",
    action: "breakdance_save_global_block",
    dataToPost: {
      ...globalBlock,
      tree: fpJsonStringify(globalBlock.tree)
    },
    decoder: GlobalBlockV,
    resultKey: "block"
  });
}

export function canSaveAsGlobalBlock(nodeToSaveId: number) {
  const nodeToSave = findNode(documentStore.document.tree, nodeToSaveId);

  if (!nodeToSave) return false;

  if (nodeToSave.data.type === GLOBAL_BLOCK_SLUG) return false;

  /*
    is it allowed inside the root or inside a div?

    when converting a node to a global block
    a tree for the global block will be created. the node will be placed in
    the tree root. if that's not allowed, it will be wrapped with a div
    so as long as the node can be placed inside a div, it's legal to convert it
    to a global block
    */

  const tempTree = getBlankTree();
  const tempDiv = createNode(tempTree, {
    type: DIV_SLUG,
    properties: null
  });
  moveNode(
    tempTree,
    tempDiv.id,
    tempTree.root.id,
    tempTree.root.children.length,
    "breakdance_block"
  );

  if (
    isThisNodeAllowedInsideThatNode(
      tempTree,
      nodeToSave,
      tempDiv.id,
      "breakdance_block"
    )
  ) {
    return true;
  }

  if (
    isThisNodeAllowedInsideThatNode(
      tempTree,
      nodeToSave,
      tempTree.root.id,
      "breakdance_block"
    )
  ) {
    return true;
  }

  return false;
}
